import type { AvailableRouterMethod, NitroFetchOptions, NitroFetchRequest, TypedInternalResponse } from "nitropack";

export async function useAPI<T = unknown, R extends NitroFetchRequest = NitroFetchRequest>(
  request: R,
  opts?: NitroFetchOptions<R, AvailableRouterMethod<R>> & {
    customCallbacks?: {
      onAuthFailure?: () => void;
    };
  }
): Promise<TypedInternalResponse<R, T>> {
  const baseURL = useRuntimeConfig().public.apiBaseURL;
  const token = useUserToken().value;
  const loginAsUser = useLoginAsUser();

  function onAuthFailure() {
    return navigateToLogin({ errorCode: "session-expired" });
  }

  // Check if this is a profile config endpoint
  const isProfileConfigEndpoint = typeof request === "string" && request.includes("/profile/config");

  const customFetch = $fetch.create({
    baseURL,
    headers: {
      // not send Authorization: undefined for not logged in users (sharing)
      ...(token && { Authorization: `Bearer ${token}` }),
      // Add x-login-as header if user is loginAsUser is provided
      // Exclude X-Login-As header for profile config endpoint
      ...(loginAsUser.state?.id && !isProfileConfigEndpoint && { "X-Login-As": loginAsUser.state.id }),
    },
    onRequestError() {
      // Since we can't access the status code here and check for 401 - we're assuming that any request error is a session error,
      // until we will have a fix by DevOps team for ISTIO service: https://github.com/istio/istio/issues/29425
      onAuthFailure();
    },
    onResponseError({ response }) {
      if (response.status === 401) {
        if (opts?.customCallbacks?.onAuthFailure) {
          opts.customCallbacks.onAuthFailure();
        } else {
          onAuthFailure();
        }
      }
    },
  });

  return customFetch(request, opts);
}
